import { Border, BoxConstraints, RenderBox, RenderedAnnotation } from '../rendering'
import { renderElement } from '../document'

type TextFieldProps = {
  name: string
  value: string
  fontSize?: number
  multiline?: boolean
  border?: Border
  backgroundColor?: string
  maskChar?: string
  onChange?: (value: string) => void
}

export class TextFieldRenderBox extends RenderBox<TextFieldProps> {
  performLayout(c: BoxConstraints): void {
    this.box = { x: 0, y: 0, ...fillSize(c) }
  }

  annotation(): RenderedAnnotation {
    return {
      type: 'text_field',
      bounds: this.box,
      name: this.props.name,
      value: this.props.value,
      font_size: this.props.fontSize,
      multiline: this.props.multiline ?? false,
      border: this.props.border,
      background_color: this.props.backgroundColor,
      mask_char: this.props.maskChar,
      onChange: this.props.onChange
    }
  }
}

export function TextField(props: TextFieldProps) {
  return renderElement(new TextFieldRenderBox(props))
}

export function HiddenTextField({
  name,
  value,
  onChange
}: {
  name: string
  value: string
  onChange?: (value: string) => void
}) {
  return renderElement(new HiddenTextFieldRenderBox({ name, value, onChange }))
}

export class HiddenTextFieldRenderBox extends RenderBox<{
  name: string
  value: string
  onChange?: (value: string) => void
}> {
  performLayout(): void {
    this.box = { x: 0, y: 0, width: 0, height: 0 }
  }

  annotation(): RenderedAnnotation {
    return {
      type: 'text_field',
      bounds: this.box,
      name: this.props.name,
      value: this.props.value,
      multiline: false,
      onChange: this.props.onChange
    }
  }
}

type CheckboxProps = {
  name: string
  checked: boolean
  border?: Border
  backgroundColor?: string
  onChange?: (checked: boolean) => void
}

export class CheckboxRenderBox extends RenderBox<CheckboxProps> {
  performLayout(c: BoxConstraints): void {
    this.box = { x: 0, y: 0, ...BoxConstraints.constrain(c, { width: 10, height: 10 }) }
  }

  annotation(): RenderedAnnotation {
    return {
      type: 'checkbox',
      bounds: this.box,
      name: this.props.name,
      checked: this.props.checked,
      border: this.props.border,
      background_color: this.props.backgroundColor,
      onChange: this.props.onChange
    }
  }
}

export function Checkbox(props: CheckboxProps) {
  return renderElement(new CheckboxRenderBox(props))
}

type RadioProps = {
  name: string
  value: string | boolean | number
  exportValue?: string
  selected: boolean
  backgroundColor?: string
  onChange?: (selected: boolean) => void
}

export class RadioRenderBox extends RenderBox<RadioProps> {
  performLayout(c: BoxConstraints): void {
    this.box = { x: 0, y: 0, ...BoxConstraints.constrain(c, { width: 10, height: 10 }) }
  }

  annotation(): RenderedAnnotation {
    return {
      type: 'radio',
      bounds: this.box,
      name: this.props.name,
      value: this.props.value.toString(),
      export_value: this.props.exportValue ?? this.props.value.toString(),
      selected: this.props.selected,
      background_color: this.props.backgroundColor,
      onChange: this.props.onChange
    }
  }
}

export function Radio(props: RadioProps) {
  return renderElement(new RadioRenderBox(props))
}

type SelectProps = {
  name: string
  value?: string
  options: Array<{ value: string; label: string }>
  border?: Border
  backgroundColor?: string
  onChange?: (value: string) => void
}

export class SelectRenderBox extends RenderBox<SelectProps> {
  performLayout(c: BoxConstraints): void {
    this.box = { x: 0, y: 0, ...fillSize(c) }
  }

  annotation(): RenderedAnnotation {
    return {
      type: 'select',
      bounds: this.box,
      name: this.props.name,
      value: this.props.value,
      options: this.props.options,
      border: this.props.border,
      background_color: this.props.backgroundColor,
      onChange: this.props.onChange
    }
  }
}

export function Select(props: SelectProps) {
  return renderElement(new SelectRenderBox(props))
}

function fillSize(c: BoxConstraints) {
  return {
    width: Number.isFinite(c.maxWidth) ? c.maxWidth : c.minWidth,
    height: c.minHeight > 0 ? c.minHeight : Number.isFinite(c.maxHeight) ? c.maxHeight : 0
  }
}
